/**
 * Laufende Ereignisse als Handlungsaufforderung direkt unter der Kopfzeile.
 *
 * Ereignisse verlangen eine Entscheidung – sie gehören deshalb nicht in eine
 * Unterseite, sondern dorthin, wo der Spieler ohnehin hinsieht.
 */
import { useState } from 'react';
import clsx from 'clsx';
import type { DdosAttack, GameEvent } from '@hosting-sim/core';
import { Badge, Button, Modal, Notice, Stat, StatGrid, type Tone } from '../ui';
import { ServerConsole } from '../ServerConsole';
import { MinigameOverlay } from '../minigames/MinigameOverlay';
import { MINIGAME_LABEL } from '@hosting-sim/core';
import { useActiveEvents, useGame } from '../../store/game';
import { money, duration } from '../../lib/format';

export function EventBanner() {
  const events = useActiveEvents();
  const run = useGame((s) => s.run);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  // Das Spiel bleibt offen, auch wenn das Ereignis durch den Sieg endet.
  const [playingEvent, setPlayingEvent] = useState<GameEvent | null>(null);
  // Live nachgeführt: Ein Angriff ändert sich jede Stunde, der Dialog soll mitgehen.
  const selected = events.find((e) => e.id === selectedId) ?? null;

  const urgent = events.filter((e) => e.severity === 'critical' || e.severity === 'error');
  const shown = urgent.length > 0 ? urgent : events;
  const overlay =
    playingId && playingEvent ? (
      <MinigameOverlay
        event={playingEvent}
        onClose={() => {
          setPlayingId(null);
          setPlayingEvent(null);
        }}
      />
    ) : null;
  if (shown.length === 0) return overlay;

  const primary = shown[0];

  return (
    <>
      <div
        className={clsx(
          'flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm',
          primary.severity === 'critical'
            ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/60 dark:text-red-200'
            : primary.severity === 'error'
              ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200'
              : 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/50 dark:text-sky-200',
        )}
      >
        <Badge tone={primary.severity === 'critical' ? 'danger' : primary.severity === 'error' ? 'warning' : 'info'}>
          {primary.severity === 'critical' ? 'Kritisch' : primary.severity === 'error' ? 'Störung' : 'Hinweis'}
        </Badge>
        <span className="min-w-0 flex-1 truncate font-medium">{primary.title}</span>
        {primary.ticksRemaining > 0 && (
          <span className="text-xs opacity-80">noch {duration(primary.ticksRemaining)}</span>
        )}
        {shown.length > 1 && <span className="text-xs opacity-80">+{shown.length - 1} weitere</span>}
        {/* Jedes spielbare Ereignis direkt erreichbar – sonst geht das dritte im Dialog unter. */}
        {events
          .filter((e) => e.minigame && (e.minigame.state === 'ready' || e.minigame.state === 'running'))
          .map((e) => (
            <Button
              key={e.id}
              size="sm"
              variant="primary"
              title={e.title}
              onClick={() => {
                setPlayingEvent(e);
                setPlayingId(e.id);
              }}
            >
              ▶ {MINIGAME_LABEL[e.minigame!.kind]}
            </Button>
          ))}
        <Button size="sm" variant="secondary" onClick={() => setSelectedId(primary.id)}>
          Details
        </Button>
      </div>

      <Modal
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={selected?.title ?? ''}
        wide
        footer={
          <Button variant="ghost" onClick={() => setSelectedId(null)}>
            Schließen
          </Button>
        }
      >
        {selected && (
          <div className="space-y-4">
            <p className="text-sm text-panel-600 dark:text-panel-300">{selected.description}</p>

            {selected.ddos && <AttackPanel attack={selected.ddos} />}

            {selected.minigame && (
              <div
                className={clsx(
                  'flex flex-wrap items-center gap-3 rounded-lg border p-3',
                  selected.minigame.state === 'ready' || selected.minigame.state === 'running'
                    ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
                    : 'border-panel-200 dark:border-panel-700',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Selbst eingreifen: {MINIGAME_LABEL[selected.minigame.kind]}</p>
                  <p className="text-xs text-panel-500 dark:text-panel-400">
                    {selected.minigame.state === 'ready'
                      ? 'Kostenlos, aber nur ein Versuch. Solange du spielst, ist das Ereignis angehalten.'
                      : selected.minigame.state === 'running'
                        ? 'Läuft – das Ereignis ist angehalten.'
                        : selected.minigame.resultNote}
                  </p>
                </div>
                {(selected.minigame.state === 'ready' || selected.minigame.state === 'running') && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      setPlayingEvent(selected);
                      setPlayingId(selected.id);
                      setSelectedId(null);
                    }}
                  >
                    {selected.minigame.state === 'ready' ? 'Minispiel starten' : 'Weiterspielen'}
                  </Button>
                )}
              </div>
            )}

            {selected.options.length === 0 ? (
              <p className="text-xs text-panel-500">
                Für dieses Ereignis gibt es keine direkte Gegenmaßnahme – es läuft nach einiger Zeit von selbst aus.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="label">Mögliche Reaktionen</p>
                {selected.options.map((option) => (
                  <div key={option.key} className="surface-muted flex flex-wrap items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{option.label}</p>
                      <p className="text-xs text-panel-500 dark:text-panel-400">{option.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="tabular text-sm font-medium">{option.cost > 0 ? money(option.cost) : 'kostenlos'}</p>
                      <p className="text-2xs text-panel-500">Erfolg {Math.round(option.successChance * 100)} %</p>
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      loading={busy}
                      onClick={async () => {
                        setBusy(true);
                        await run({ type: 'event/resolve', eventId: selected.id, optionKey: option.key });
                        setBusy(false);
                        // Einen Angriff will man nach der Maßnahme weiter beobachten.
                        if (!selected.ddos) setSelectedId(null);
                      }}
                    >
                      Ausführen
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {shown.length > 1 && (
              <div>
                <p className="label mb-1">Weitere laufende Ereignisse</p>
                <ul className="space-y-1">
                  {shown
                    .filter((e) => e.id !== selected.id)
                    .map((event) => (
                      <li key={event.id}>
                        <button className="link text-xs" onClick={() => setSelectedId(event.id)}>
                          {event.title}
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
      {overlay}
    </>
  );
}

const TARGET_STATE: Record<DdosAttack['targetState'], { label: string; tone: Tone }> = {
  ok: { label: 'erreichbar', tone: 'success' },
  degraded: { label: 'angeschlagen', tone: 'warning' },
  down: { label: 'nicht erreichbar', tone: 'danger' },
  blackholed: { label: 'per Blackhole verworfen', tone: 'muted' },
};

function gbps(value: number): string {
  return `${value.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Gbit/s`;
}

/** Messwerte des laufenden Angriffs und die Konsole des Ziels. */
function AttackPanel({ attack }: { attack: DdosAttack }) {
  const world = useGame((s) => s.world);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const server = attack.serverId ? world?.servers[attack.serverId] : undefined;
  // Vor dem ersten Tick gibt es noch keine Messung – „erreichbar“ wäre geraten.
  const measuring = attack.currentGbps === 0 && attack.filteredGbps === 0 && attack.residualGbps === 0;
  const state = measuring ? { label: 'Messung läuft', tone: 'info' as Tone } : TARGET_STATE[attack.targetState];

  return (
    <div className="space-y-3">
      <StatGrid cols={4}>
        <Stat label="Angriff jetzt" value={gbps(attack.currentGbps)} hint={`Spitze ${gbps(attack.peakGbps)}`} />
        <Stat label="Gefiltert" value={gbps(attack.filteredGbps)} hint={attack.externalScrub ? 'externes Scrubbing' : 'am Netzrand'} />
        <Stat
          label="Auf den Zuleitungen"
          value={gbps(attack.residualGbps)}
          tone={attack.residualGbps > 0 ? 'warning' : 'neutral'}
          hint="landet im 95. Perzentil"
        />
        <Stat
          label="Zuleitungen"
          value={`${Math.round(attack.uplinkSaturation * 100)} %`}
          tone={attack.uplinkSaturation > 1 ? 'negative' : attack.uplinkSaturation > 0.9 ? 'warning' : 'neutral'}
          hint={attack.uplinkSaturation > 1 ? 'alle Kunden am Standort leiden' : 'Auslastung'}
        />
      </StatGrid>

      <Notice tone={state.tone === 'muted' ? 'info' : state.tone} title={`Ziel ${attack.targetIp}: ${state.label}`}>
        {measuring ? 'Die ersten Messwerte liegen nach der nächsten Spielstunde vor.' : attack.targetNote}
        {attack.externalScrub && ` Kosten: ${attack.scrubPricePerGbps} € je Gbit/s und Stunde.`}
      </Notice>

      {server && (
        <div>
          <Button size="sm" variant="secondary" onClick={() => setConsoleOpen((open) => !open)}>
            {consoleOpen ? 'Konsole schließen' : `Konsole von ${server.name} öffnen`}
          </Button>
          {consoleOpen && <ServerConsole server={server} className="mt-2" />}
        </div>
      )}
    </div>
  );
}
